import type {
  GetServerSidePropsContext,
  GetServerSidePropsResult,
  NextPage,
} from "next";
import { MainLayout } from "src/presentation/layouts";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { NextSeo } from "next-seo";
import { useTranslation } from "next-i18next";
import { settings } from "@config/settings";
import { MainRouter } from "@config/urls";
import { CountryLocationService } from "@services/country-location-service";
import { EntitiesFetcherService } from "@services/entities-fetcher";
import { localesParserDictionary } from "@utils/locales-parser";
import { useRef } from "react";
import styles from "./croptology-page.module.scss";
import LabIcon from "@assets/icons/croptology - asesoramiento_distribuidor soporte.svg";
import CalendarIcon from "@assets/icons/croptology - jornadas.svg";
import MaterialIcon from "@assets/icons/croptology - material tecnico.svg";
import FreeIcon from "@assets/icons/free.svg";

import { TemplateText, Text } from "src/presentation/components/atoms";
import { ContactForm } from "src/presentation/components/organisms";

const Home: NextPage = () => {
  const { t } = useTranslation();
  const formRef = useRef(null);

  const scrollToRef = (ref: any) => window.scrollTo(0, ref.current.offsetTop);
  const ventajas = [
    {
      key: "asesoramiento",
      icon: <LabIcon />,
    },
    {
      key: "jornadas",
      icon: <CalendarIcon />,
    },
    {
      key: "material",
      icon: <MaterialIcon />,
    },
  ];
  const TopBanner = () => {
    return (
      <section
        style={{
          backgroundImage: `url(/static/images/nosotros-croptology-header-manvert-bioestimulantes.jpg)`,
        }}
        className={styles.topbanner}
      >
        <div className={styles.card} style={{ justifyContent: "flex-end" }}>
          <Text as="h1">{t("CroptologyView.topbanner.heading")}</Text>
          <TemplateText template="small">
            {t("CroptologyView.topbanner.p1")}
          </TemplateText>
          <TemplateText template="small">
            {t("CroptologyView.topbanner.p2")}
          </TemplateText>
          <button onClick={() => scrollToRef(formRef)}>
            {t("CroptologyView.topbanner.link1")}
          </button>
        </div>
      </section>
    );
  };

  const ProyectoSection = () => {
    return (
      <section className={styles.proyectoSection}>
        <Text as="h2">{t("CroptologyView.proyectoSection.heading")}</Text>
        <TemplateText template="small">
          {t("CroptologyView.proyectoSection.p1")}
        </TemplateText>
        <div className={styles.items}>
          <FreeIcon />
          <ul>
            <li>{t("CroptologyView.proyectoSection.ul.li1")}</li>
            <li>{t("CroptologyView.proyectoSection.ul.li2")}</li>
            <li>{t("CroptologyView.proyectoSection.ul.li3")}</li>
            <li>{t("CroptologyView.proyectoSection.ul.li4")}</li>
          </ul>
        </div>
      </section>
    );
  };
  const VentajasSection = () => {
    return (
      <section className={styles.ventajasSection}>
        <Text as="h2">{t("CroptologyView.ventajasSection.heading")}</Text>
        <div className={styles.ventajaslist}>
          {ventajas.map((item) => {
            return (
              <div key={item.key} className={styles.ventaja}>
                {item.icon}
                <Text as="h3">
                  {t(`CroptologyView.ventajasSection.items.${item.key}.title`)}
                </Text>
                <TemplateText template="small">
                  {t(
                    `CroptologyView.ventajasSection.items.${item.key}.description`
                  )}
                </TemplateText>
              </div>
            );
          })}
        </div>
      </section>
    );
  };

  const ContactFormSection = () => {
    return (
      <section ref={formRef} className={styles.formSection}>
        <ContactForm
          title={t("CroptologyView.formSection.heading") || ""}
          cultivos
          actividad
          empresa
          empresaOblig={false}
          infoComercialOblig
          comentarios={false}
          link="https://go.manvert.com/l/939633/2021-08-03/fjtqd9"
          GDPRTextVersion="croptology"
          eventAction="EnviarSolicitud"
          eventLabel="Formulario Croptology"
          croptology={true}
        />
      </section>
    );
  };
  return (
    <>
      <NextSeo
        title={t("SEOMeta.CroptologyView.title") || ""}
        description={t("SEOMeta.CroptologyView.description") || ""}
      />
      <MainLayout
        breadcrumb={[
          {
            label: t("urls.home"),
            url: MainRouter.home.generatePath(),
          },
          {
            label: t("urls.nosotros"),
            url: MainRouter.nosotros.generatePath(),
          },
          {
            label: t("urls.croptology"),
            url: MainRouter["croptology"].generatePath(),
          },
        ]}
      >
        <div className={styles.root}>
          <TopBanner />
          <ProyectoSection />
          <VentajasSection />
          <ContactFormSection />
        </div>
      </MainLayout>
    </>
  );
};

export async function getServerSideProps(
  Props: GetServerSidePropsContext
): Promise<GetServerSidePropsResult<any>> {
  const { locale, req, res } = Props;

  let selectedCountry =
    await new CountryLocationService().getSelectedCountryData(req, res);

  const opts = {
    locale: localesParserDictionary[locale || settings.DEFAULT_LANG],
    country: selectedCountry.countrycode,
  };

  const entities = await new EntitiesFetcherService().getEntities({
    ...opts,
    productosDestacados: true,
    productosGenericos: true,
  });

  return {
    props: {
      country: selectedCountry.countrycode,
      productosGenericos: JSON.stringify(entities.productosGenericosData),
      productosDestacadosData: JSON.stringify(entities.productosDestacadosData),
      ...(await serverSideTranslations(locale || settings.I18N_FALLBACK, [
        "common",
      ])),
    },
  };
}

export default Home;
